// CreateSpecialAchatModal.jsx

import React, { useEffect, useMemo, useState, useRef } from "react";
import { FaSave, FaTimes, FaPlus, FaTrash } from "react-icons/fa";
import Button from "../../../components/Button/Button";
import { getAllUsers } from "../../../services/userServices";
import { getArticles } from "../../../services/articlesServices";
import { createSpecialAchat } from "../../../services/achatsServices";
import { getSettings } from "../../../services/settingsServices";
import { sendNotification } from "../../../services/notificationServices";
import { toast } from "react-toastify";
import Select from "react-select";

const CreateSpecialAchatModal = ({ isActive, onClose, refreshAchats }) => {
  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [selectedArticles, setSelectedArticles] = useState([
    { article_id: "", quantite: "1" },
  ]);
  const [saison, setSaison] = useState("");
  const [seasons, setSeasons] = useState([]);
  const [defaultPhotoUrl, setDefaultPhotoUrl] = useState("");
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);

  // 🔽 Réduction globale (sans passer par un article saisi par l’admin)
  const [applyDiscount, setApplyDiscount] = useState(false);
  const [discountType, setDiscountType] = useState("percent"); // "percent" | "fixed"
  const [discountValue, setDiscountValue] = useState(""); // string pour saisie
  const [chosenDiscountArticleId, setChosenDiscountArticleId] = useState("");

  const dialogRef = useRef(null);

  // Helpers saisons
  const getPreviousSeason = (currentSeason) => {
    const [startYear, endYear] = currentSeason.split("-").map(Number);
    return `${startYear - 1}-${endYear - 1}`;
  };
  const getNextSeason = (currentSeason) => {
    const [startYear, endYear] = currentSeason.split("-").map(Number);
    return `${startYear + 1}-${endYear + 1}`;
  };

  // Arrondir à l'euro le plus proche (valeur absolue → entier), puis on remet le signe après
  const roundEuro = (x) => Math.round(x);

  // Chargement à l'ouverture
  useEffect(() => {
    if (!isActive) return;
    (async () => {
      try {
        setLoading(true);
        const [usersData, articlesData, settingsData] = await Promise.all([
          getAllUsers(),
          getArticles(),
          getSettings(),
        ]);
        setUsers(usersData || []);
        setArticles(articlesData || []);

        const settings = settingsData?.[0] || {};
        const activeSeason = settings.saison_active || "";
        if (activeSeason) {
          setSaison(activeSeason);
          setSeasons([
            getPreviousSeason(activeSeason),
            activeSeason,
            getNextSeason(activeSeason),
          ]);
        }
        setDefaultPhotoUrl(settings.photo_url_default_lic_cot || "");
        setToken(localStorage.getItem("token") || "");

        // Focus sur le Select utilisateur à l'ouverture
        setTimeout(() => {
          const control =
            dialogRef.current?.querySelector(".rs__control input");
          control?.focus();
        }, 0);
      } catch (e) {
        console.error(e);
        toast.error("Erreur lors du chargement des données.");
      } finally {
        setLoading(false);
      }
    })();
  }, [isActive]);

  // Échap pour fermer
  useEffect(() => {
    if (!isActive) return;
    const onEsc = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [isActive, onClose]);

  // Options Utilisateurs
  const userOptions = useMemo(
    () =>
      users.map((u) => {
        const adherentsLabel = u.adherents?.length
          ? ` (${u.adherents
              .map((a) => `${a.prenom.trim()} ${a.nom}`)
              .join(", ")})`
          : "";
        return { value: u.id, label: `${u.nom} ${u.prenom}${adherentsLabel}` };
      }),
    [users]
  );

  // Options Articles (affichage: nom seul)
  const articleOptions = useMemo(
    () =>
      articles.map((a) => ({
        value: a.id,
        label: a.nom,
        price:
          Number(
            String(a.prix ?? 0)
              .toString()
              .replace(",", ".")
          ) || 0,
        image: a.photo_url || defaultPhotoUrl,
      })),
    [articles, defaultPhotoUrl]
  );

  // 🔎 Détection d’un article “réduction”
  const isReductionArticleName = (name = "") =>
    /reduction_foyer_\d+/i.test(name) ||
    /-?\d+(?:[.,]\d+)?\s*%/.test(name) ||
    /réduction|remise|discount/i.test(name);

  const reductionCandidates = useMemo(
    () =>
      articles
        .filter(
          (a) =>
            isReductionArticleName(a.nom || "") ||
            (Number(
              String(a.prix ?? 0)
                .toString()
                .replace(",", ".")
            ) || 0) < 0
        )
        .map((a) => ({
          value: a.id,
          label: `${a.nom} (PU ${
            Number(
              String(a.prix ?? 0)
                .toString()
                .replace(",", ".")
            ) || 0
          }€)`,
        })),
    [articles]
  );

  const extractReductionPercent = (name = "") => {
    const byKey = name.match(/reduction_foyer_(\d+)/i);
    if (byKey) return parseFloat(byKey[1]);
    const byPercent = name.match(/(-?\d+(?:[.,]\d+)?)\s*%/);
    if (byPercent)
      return Math.abs(parseFloat(String(byPercent[1]).replace(",", ".")));
    return null;
  };

  // Total (live) tel que saisi (N’APPLIQUE PAS les futurs recalculs % ni la réduction globale)
  const baseTotal = useMemo(() => {
    return selectedArticles.reduce((sum, row) => {
      const opt = articleOptions.find(
        (o) => String(o.value) === String(row.article_id)
      );
      const unit = opt?.price || 0;
      const q = parseInt(row.quantite || "0", 10) || 0;
      return sum + unit * q;
    }, 0);
  }, [selectedArticles, articleOptions]);

  // Aperçu réduction globale (sans modifier les lignes visibles)
  const discountPreview = useMemo(() => {
    if (!applyDiscount) return 0;
    const val = Number(String(discountValue).replace(",", ".")) || 0;
    if (val <= 0) return 0;

    // total des lignes SAUF celles déjà “réduction” (pour éviter double-discount)
    const totalSansReductions = selectedArticles.reduce((sum, row) => {
      const ref = articles.find((a) => String(a.id) === String(row.article_id));
      const isReduc = ref ? isReductionArticleName(ref.nom || "") : false;
      if (isReduc) return sum;
      const unit =
        Number(
          String(ref?.prix ?? 0)
            .toString()
            .replace(",", ".")
        ) || 0;
      const q = parseInt(row.quantite || "0", 10) || 0;
      return sum + unit * q;
    }, 0);

    if (discountType === "percent") {
      const abs = (totalSansReductions * val) / 100;
      // on arrondit la remise (valeur absolue) à l'euro, puis on renvoie une valeur négative
      return -roundEuro(Math.abs(abs));
    }
    // fixed
    return -Math.abs(val);
  }, [applyDiscount, discountType, discountValue, selectedArticles, articles]);

  // Actions lignes
  const handleAddArticle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedArticles((prev) => [...prev, { article_id: "", quantite: "1" }]);
  };

  const handleArticleChange = (index, key, value) =>
    setSelectedArticles((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [key]: value } : row))
    );

  // --- Gestion quantité (compatible iOS) ---
  const handleQtyChange = (index, raw) => {
    if (/^\d*$/.test(raw)) {
      setSelectedArticles((prev) =>
        prev.map((row, i) => (i === index ? { ...row, quantite: raw } : row))
      );
    }
  };
  const handleQtyBlur = (index) => {
    setSelectedArticles((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const n = parseInt(row.quantite || "0", 10);
        const normalized = Number.isFinite(n) && n > 0 ? n : 1;
        return { ...row, quantite: String(normalized) };
      })
    );
  };
  const changeQtyBy = (index, delta) => {
    setSelectedArticles((prev) =>
      prev.map((row, i) => {
        if (i !== index) return row;
        const n = parseInt(row.quantite || "0", 10) || 0;
        const next = Math.max(1, n + delta);
        return { ...row, quantite: String(next) };
      })
    );
  };

  const handleRemoveArticle = (index) =>
    setSelectedArticles((prev) =>
      prev.length === 1 ? prev : prev.filter((_, i) => i !== index)
    );

  // Submit + notif
  const handleSendNotification = async (recipientId) => {
    const message =
      "Le responsable du club vous a affecté un nouveau règlement de licence-cotisation à régler sur la page Vos Commandes & Règlements.";
    await sendNotification(message, recipientId, token);
  };

  const resetForm = () => {
    setSelectedUser("");
    setSelectedArticles([{ article_id: "", quantite: "1" }]);
    setApplyDiscount(false);
    setDiscountType("percent");
    setDiscountValue("");
    setChosenDiscountArticleId("");
  };

  // --- Ajuste automatiquement les lignes “réduction_foyer_%” (quantité) ---
  const computeArticlesWithReductionPercentLines = (rows) => {
    const finalRows = [...rows];

    finalRows.forEach((line, idx) => {
      const ref = articles.find(
        (a) => String(a.id) === String(line.article_id)
      );
      if (!ref) return;
      const pct = extractReductionPercent(ref.nom || "");
      if (!pct) return;

      // Total des autres lignes (hors cette ligne)
      const totalBrut = finalRows.reduce((sum, r, i) => {
        if (i === idx) return sum;
        const ref2 = articles.find(
          (a) => String(a.id) === String(r.article_id)
        );
        if (!ref2) return sum;
        const unit =
          Number(
            String(ref2.prix ?? 0)
              .toString()
              .replace(",", ".")
          ) || 0;
        const q = parseInt(r.quantite || "0", 10) || 0;
        return sum + unit * q;
      }, 0);

      const abs = (totalBrut * pct) / 100;
      // Arrondi à l’euro le plus proche, puis on met le signe négatif
      const reductionValue = -roundEuro(Math.abs(abs));
      const unitPrice =
        Number(
          String(ref.prix ?? 0)
            .toString()
            .replace(",", ".")
        ) || 0;
      const base = unitPrice !== 0 ? unitPrice : -1;
      const qty = reductionValue / base;

      finalRows[idx] = {
        ...line,
        quantite: String(Number(qty.toFixed(2))), // on garde string pour cohérence
      };
    });

    return finalRows;
  };

  // Injection d’une réduction globale (utilise un “article de réduction” existant)
  const injectGlobalDiscountRow = (rows) => {
    if (!applyDiscount) return rows;

    const val = Number(String(discountValue).replace(",", ".")) || 0;
    if (val <= 0) return rows;

    const discountArticleId =
      chosenDiscountArticleId ||
      (reductionCandidates.length ? reductionCandidates[0].value : "");

    if (!discountArticleId) {
      toast.error(
        "Aucun article de réduction trouvé. Créez-en un (prix unitaire négatif) ou sélectionnez-en un."
      );
      throw new Error("No reduction article available");
    }

    const discountRef = articles.find(
      (a) => String(a.id) === String(discountArticleId)
    );
    const unitPrice =
      Number(
        String(discountRef?.prix ?? 0)
          .toString()
          .replace(",", ".")
      ) || 0;

    if (unitPrice === 0) {
      toast.error(
        "L’article de réduction choisi a un prix unitaire nul. Corrigez son prix."
      );
      throw new Error("Reduction article unit price is zero");
    }

    // Total des lignes hors toute “réduction”
    const totalSansReductions = rows.reduce((sum, r) => {
      const ref = articles.find((a) => String(a.id) === String(r.article_id));
      if (!ref) return sum;
      if (isReductionArticleName(ref.nom || "")) return sum;
      const unit =
        Number(
          String(ref.prix ?? 0)
            .toString()
            .replace(",", ".")
        ) || 0;
      const q = parseInt(r.quantite || "0", 10) || 0;
      return sum + unit * q;
    }, 0);

    const discountAmount =
      discountType === "percent"
        ? // % → on arrondit à l’euro
          (() => {
            const abs = (totalSansReductions * val) / 100;
            return -roundEuro(Math.abs(abs));
          })()
        : -Math.abs(val); // montant fixe inchangé

    const qty = discountAmount / unitPrice; // peut être décimal

    // Si une ligne pour cet article existe déjà -> on additionne
    const existIndex = rows.findIndex(
      (r) => String(r.article_id) === String(discountArticleId)
    );

    if (existIndex !== -1) {
      const existQ = Number(rows[existIndex].quantite || "0") || 0;
      const newQ = existQ + qty;
      const next = [...rows];
      next[existIndex] = {
        ...next[existIndex],
        quantite: String(Number(newQ.toFixed(2))),
      };
      return next;
    }

    return [
      ...rows,
      {
        article_id: String(discountArticleId),
        quantite: String(Number(qty.toFixed(2))),
      },
    ];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedUser) return toast.warn("Sélectionnez un utilisateur.");
    const filled = selectedArticles.filter(
      (r) => r.article_id && (parseInt(r.quantite || "0", 10) || 0) > 0
    );
    if (filled.length === 0) return toast.warn("Ajoutez au moins un article.");

    try {
      setLoading(true);

      // 1) Applique la logique des lignes “réduction_foyer_%”
      let rows = computeArticlesWithReductionPercentLines(filled);

      // 2) Injecte la réduction globale si demandée
      rows = injectGlobalDiscountRow(rows);

      const achatData = {
        user_achat_id: selectedUser,
        saison,
        articles: rows.map((row) => ({
          article_id: row.article_id,
          // en backend, accepter quantités décimales/négatives pour les lignes de remise
          quantite: Number(String(row.quantite).replace(",", ".")) || 1,
          photo_url:
            articleOptions.find(
              (o) => String(o.value) === String(row.article_id)
            )?.image || defaultPhotoUrl,
        })),
      };

      await createSpecialAchat(achatData);
      await handleSendNotification(selectedUser);
      toast.success("Règlement Licence-Cotisation ajouté avec succès.");
      refreshAchats?.();
      resetForm();
      onClose?.();
    } catch (error) {
      console.error(error);
      if (error?.message !== "No reduction article available") {
        toast.error(
          "Erreur lors de la création du règlement Licence-Cotisation."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isActive) return null;

  const compactSelectStyles = {
    container: (base) => ({
      ...base,
      minWidth: 0,
      width: "100%",
    }),
    control: (base) => ({
      ...base,
      minHeight: 34,
      borderRadius: 12,
      backgroundColor: "transparent",
    }),
    valueContainer: (base) => ({
      ...base,
      paddingTop: 2,
      paddingBottom: 2,
      paddingLeft: 8,
      paddingRight: 6,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      paddingTop: 2,
      paddingBottom: 2,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Ajouter un règlement Licence-Cotisation-Réduction"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />

      {/* Dialog */}
      <div
        ref={dialogRef}
        className="relative w-full max-w-3xl flex flex-col overflow-hidden overflow-x-hidden rounded-2xl shadow-2xl ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white dark:bg-slate-900 max-h-[85vh] md:max-h-[calc(100vh-3rem)]"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-blue-500 via-violet-500 to-pink-500 px-5 py-4 flex items-center justify-between">
          <h3 className="text-white font-semibold">
            Ajouter un règlement Licence-Cotisation-Réduction
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center gap-2 rounded-xl bg-white/10 px-3 py-1.5 text-white hover:bg-white/20"
            title="Fermer"
            aria-label="Fermer"
          >
            <FaTimes />
            <span className="hidden sm:inline">Fermer</span>
          </button>
        </div>

        {/* Body */}
        <form
          onSubmit={handleSubmit}
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target.tagName !== "TEXTAREA")
              e.preventDefault();
          }}
          className="grow overflow-y-auto overflow-x-auto p-5 space-y-5 text-slate-800 dark:text-slate-100"
        >
          {/* Utilisateur + Saison */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs mb-1 opacity-70">
                Utilisateur
              </label>
              <Select
                className="w-full"
                classNamePrefix="rs"
                options={userOptions}
                value={
                  userOptions.find((u) => u.value === selectedUser) || null
                }
                onChange={(sel) => setSelectedUser(sel?.value || "")}
                placeholder="Sélectionnez un utilisateur…"
                isClearable
                menuPortalTarget={document.body}
                styles={compactSelectStyles}
                isDisabled={loading}
              />
            </div>
            <div>
              <label className="block text-xs mb-1 opacity-70">Saison</label>
              <select
                className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                value={saison}
                onChange={(e) => setSaison(e.target.value)}
                required
              >
                {seasons.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Lignes articles */}
          <div className="space-y-2">
            <label className="block text-xs mb-1 opacity-70">Articles</label>
            <div className="overflow-x-hidden">
              <div className="w-full md:w-max md:min-w-[720px]">
                {selectedArticles.map((row, idx) => {
                  const option = articleOptions.find(
                    (o) => String(o.value) === String(row.article_id)
                  );
                  const unit = option?.price || 0;
                  const qty = parseInt(row.quantite || "0", 10) || 0;
                  const lineTotal = (unit * qty).toFixed(2);

                  return (
                    <div
                      key={`row-${idx}`}
                      className="rounded-xl border border-slate-200 dark:border-slate-700 p-2 md:p-3 min-w-0 md:min-w-[720px]"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto] md:grid-cols-[1fr_auto_auto_auto] gap-2 md:gap-3 items-center">
                        {" "}
                        {/* Article */}
                        <div className="min-w-0">
                          <Select
                            className="w-full"
                            classNamePrefix="rs"
                            options={articleOptions}
                            value={option || null}
                            onChange={(sel) =>
                              handleArticleChange(
                                idx,
                                "article_id",
                                sel?.value || ""
                              )
                            }
                            placeholder="Choisir un article…"
                            isClearable
                            menuPortalTarget={document.body}
                            styles={compactSelectStyles}
                          />
                        </div>
                        {/* Quantité */}
                        <div className="flex items-center justify-end gap-1 sm:gap-2">
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-xl w-8 h-8 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600"
                            onClick={() => changeQtyBy(idx, -1)}
                            aria-label="Diminuer la quantité"
                            title="Diminuer"
                          >
                            −
                          </button>
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="\d*"
                            className="w-12 sm:w-14 h-8 sm:h-9 text-center rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-2 text-sm"
                            value={row.quantite}
                            onChange={(e) =>
                              handleQtyChange(idx, e.target.value)
                            }
                            onBlur={() => handleQtyBlur(idx)}
                            aria-label="Quantité"
                            placeholder="1"
                          />
                          <button
                            type="button"
                            className="inline-flex items-center justify-center rounded-xl w-8 h-8 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600"
                            onClick={() => changeQtyBy(idx, +1)}
                            aria-label="Augmenter la quantité"
                            title="Augmenter"
                          >
                            +
                          </button>
                        </div>
                        {/* Supprimer */}
                        <div className="flex justify-end">
                          {/* Mobile: icône seule */}
                          <button
                            type="button"
                            title="Supprimer la ligne"
                            aria-label="Supprimer la ligne"
                            onClick={() => handleRemoveArticle(idx)}
                            className="inline-flex md:hidden items-center justify-center rounded-xl w-9 h-9 bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                            disabled={selectedArticles.length === 1}
                          >
                            <FaTrash />
                          </button>
                          {/* Desktop: bouton texte */}
                          <button
                            type="button"
                            title="Supprimer la ligne"
                            onClick={() => handleRemoveArticle(idx)}
                            className="hidden md:inline-flex items-center justify-center rounded-xl px-3 py-2 bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-50"
                            disabled={selectedArticles.length === 1}
                          >
                            <FaTrash className="mr-2" />
                            Supprimer
                          </button>
                        </div>
                        {/* Total ligne (desktop) */}
                        <div className="hidden md:block text-sm md:text-right">
                          <div className="opacity-70">Total ligne</div>
                          <div className="font-semibold tabular-nums">
                            {lineTotal} €
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="pt-1">
              <Button
                text="Ajouter un article"
                icon={FaPlus}
                onClick={handleAddArticle}
                type="button"
                className="!rounded-xl gap-2"
              />
            </div>
          </div>

          {/* Zone Réduction globale (optionnelle) */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="font-medium">Réduction globale (optionnelle)</div>
              <label className="inline-flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  className="toggle toggle-primary"
                  checked={applyDiscount}
                  onChange={(e) => setApplyDiscount(e.target.checked)}
                />
                <span className="text-sm">Activer</span>
              </label>
            </div>

            {applyDiscount && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  {/* Type de remise */}
                  <div>
                    <label className="block text-xs mb-1 opacity-70">
                      Type de remise
                    </label>
                    <div className="flex rounded-xl overflow-hidden border border-slate-300 dark:border-slate-700">
                      <button
                        type="button"
                        className={`flex-1 px-3 py-2 text-sm ${
                          discountType === "percent"
                            ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                            : "bg-white dark:bg-slate-900"
                        }`}
                        onClick={() => setDiscountType("percent")}
                      >
                        Pourcentage
                      </button>
                      <button
                        type="button"
                        className={`flex-1 px-3 py-2 text-sm ${
                          discountType === "fixed"
                            ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900"
                            : "bg-white dark:bg-slate-900"
                        }`}
                        onClick={() => setDiscountType("fixed")}
                      >
                        Montant fixe
                      </button>
                    </div>
                  </div>

                  {/* Valeur */}
                  <div>
                    <label className="block text-xs mb-1 opacity-70">
                      {discountType === "percent"
                        ? "Valeur (%)"
                        : "Montant (€)"}
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="w-full rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2"
                      placeholder={discountType === "percent" ? "10" : "15"}
                      value={discountValue}
                      onChange={(e) => setDiscountValue(e.target.value)}
                    />
                  </div>

                  {/* Article de réduction utilisé côté back */}
                  <div>
                    <label className="block text-xs mb-1 opacity-70">
                      Article de réduction (utilisé pour l'injection)
                    </label>
                    <Select
                      classNamePrefix="rs"
                      className="w-full"
                      options={reductionCandidates}
                      value={
                        reductionCandidates.find(
                          (o) =>
                            String(o.value) === String(chosenDiscountArticleId)
                        ) || null
                      }
                      onChange={(sel) =>
                        setChosenDiscountArticleId(sel?.value || "")
                      }
                      placeholder="Sélectionnez un article de réduction…"
                      isClearable
                      menuPortalTarget={document.body}
                      styles={compactSelectStyles}
                    />
                  </div>
                </div>

                {/* Aperçu effet de la réduction */}
                <div className="mt-2 text-right text-sm">
                  <span className="opacity-70 mr-2">Aperçu réduction :</span>
                  <span className="font-semibold tabular-nums">
                    {discountPreview.toFixed(2)} €
                  </span>
                </div>
              </>
            )}
          </div>

          {/* Résumé totaux */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 px-4 py-3 text-sm flex items-center justify-between">
              <span className="opacity-80">Total (saisie actuelle)</span>
              <span className="font-semibold text-base tabular-nums">
                {baseTotal.toFixed(2)} €
              </span>
            </div>

            <div className="rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 px-4 py-3 text-sm flex items-center justify-between">
              <span className="opacity-80">
                Estimation total après réduction globale
              </span>
              <span className="font-semibold text-base tabular-nums">
                {(baseTotal + discountPreview).toFixed(2)} €
              </span>
            </div>
          </div>

          {/* Actions */}
          <div className="sticky bottom-0 -mx-5 px-5 pb-4 pt-3 bg-white/90 dark:bg-slate-900/90 backdrop-blur supports-[backdrop-filter]:bg-white/60 dark:supports-[backdrop-filter]:bg-slate-900/60 border-t border-slate-200 dark:border-slate-700 flex justify-end gap-2">
            {" "}
            <Button
              text="Annuler"
              icon={FaTimes}
              type="button"
              onClick={() => {
                resetForm();
                onClose?.();
              }}
              className="!rounded-xl gap-2"
            />
            <Button
              text="Enregistrer"
              icon={FaSave}
              type="submit"
              className="!rounded-xl gap-2"
              disabled={loading}
            />
          </div>
        </form>
      </div>

      {/* Scrollbars fins */}
      <style>{`
        .grow.overflow-y-auto::-webkit-scrollbar { width: 10px; }
        .grow.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.5);
          border-radius: 9999px;
          border: 3px solid transparent;
          background-clip: content-box;
        }
        .dark .grow.overflow-y-auto::-webkit-scrollbar-thumb {
          background: rgba(148,163,184,.35);
        }
        .grow.overflow-y-auto::-webkit-scrollbar-track { background: transparent; }
      `}</style>
    </div>
  );
};

export default CreateSpecialAchatModal;
